import { IGetTimeDisableValueReturn } from '@fsd-features/task/TaskForm/models/TaskForm.types';
import { IAssignmentPeriodsResponse } from '@fsd-shared/models/assignment';
import { TaskProgressInterval } from 'containers/TaskBlock/TaskBlock.types';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

export const getTimeZoneOffsetHours = (): number => {
    return new Date().getTimezoneOffset() / 60 + 3;
};
export const isDateTimeInInterval = (
    dateTime?: string,
    interval?: TaskProgressInterval,
    periodsData?: IAssignmentPeriodsResponse,
): boolean => {
    const { assignmentPeriod, blockedDates } = periodsData ?? {};
    const periods = assignmentPeriod
        ? {
              datetimeStart: assignmentPeriod.datetimeStart,
              datetimeEnd: assignmentPeriod.datetimeEnd,
          }
        : undefined;

    const { datetimeStart, datetimeEnd } = interval || periods || {};

    if (datetimeStart && datetimeEnd && dateTime) {
        const timeDJS = dayjs(dateTime);
        const datetimeStartDJS = dayjs(datetimeStart);
        const datetimeEndDJS = dayjs(datetimeEnd);
        const isDateBlocked = blockedDates?.some((item) => dayjs(item).isSame(timeDJS, 'day'));

        return timeDJS.isBetween(datetimeStartDJS, datetimeEndDJS, 'minutes', '[]') && !isDateBlocked;
    } else {
        return true;
    }
};
export const isCorrectInterval = (dateTimeStart?: string, dateTimeEnd?: string): boolean => {
    if (dateTimeEnd) {
        const dateTimeFirstDJS = dateTimeStart ? dayjs(dateTimeStart) : dayjs();
        const dateTimeSecondDJS = dayjs(dateTimeEnd);

        return dateTimeFirstDJS?.isBefore(dateTimeSecondDJS, 'minute');
    } else {
        return true;
    }
};

/**
 * @description определяем нужно ли дизейблить текущую дату
 * @param date
 * @param interval
 * @param periodData
 * @return {boolean}
 */
export const disabledDate = (
    date: Dayjs,
    interval?: { datetimeStart?: string; datetimeEnd?: string },
    periodData?: IAssignmentPeriodsResponse,
): boolean => {
    dayjs.extend(timezone);

    const { assignmentPeriod, blockedDates } = periodData ?? {};
    const periods = assignmentPeriod
        ? {
              datetimeStart: assignmentPeriod.datetimeStart,
              datetimeEnd: assignmentPeriod.datetimeEnd,
          }
        : undefined;

    const { datetimeStart, datetimeEnd } = interval || periods || {};

    let intervalStart = false;
    let intervalEnd = false;

    const currDate = dayjs().tz('Europe/Moscow');
    const start = dayjs(datetimeStart).tz('Europe/Moscow');
    const end = dayjs(datetimeEnd).tz('Europe/Moscow');

    const past = date.isBefore(currDate.format('YYYY-MM-DD'), 'day');

    if (datetimeStart) {
        intervalStart = date.isBefore(start.format('YYYY-MM-DD'), 'day');
    }

    if (datetimeEnd) {
        intervalEnd = date.isAfter(end.format('YYYY-MM-DD'), 'day');
    }
    const isDateBlocked = blockedDates?.some((item) => dayjs(item).isSame(date, 'day'));

    return past || intervalEnd || intervalStart || !!isDateBlocked;
};

/**
 * @description определяем перечень часов которые нужно задизейблить
 *              в зависимости от даты
 * @param date
 * @param value
 */
export const disabledHours = (date?: string, value?: IGetTimeDisableValueReturn): Array<number | never> => {
    const { startHour, endHour, isStartSame, isEndSame } = value || {};
    const array: Array<number | never> = [];

    if (date === undefined) {
        for (let i = 0; i <= 24; i++) {
            array.push(i);
        }
    }
    if (isStartSame) {
        for (let i = 0; i < (startHour ?? 0); i++) {
            array.push(i);
        }
    }
    if (isEndSame) {
        for (let j = (endHour ?? 24) + 1; j <= 24; j++) {
            array.push(j);
        }
    }

    return array;
};

/**
 * @description определяем перечень минут которые нужно задизейблить
 *              в зависимости от часа и даты
 * @param hour
 * @param date
 * @param value
 */
export const disabledMinutes = (
    hour: number,
    date?: string,
    value?: IGetTimeDisableValueReturn,
): Array<number | never> => {
    const { startHour, endHour, startMinute, endMinute, isStartSame, isEndSame } = value || {};
    const array: Array<number | never> = [];

    if (date === undefined) {
        for (let i = 0; i <= 60; i += 10) {
            array.push(i);
        }
    }
    if (hour == startHour) {
        if (isStartSame) {
            for (let i = 0; i < (startMinute ?? 0); i += 10) {
                array.push(i);
            }
        }
    }
    if (hour == endHour) {
        if (isEndSame) {
            const minutes = endMinute !== undefined ? endMinute + 10 : 70;
            for (let j = minutes; j <= 60; j += 10) {
                array.push(j);
            }
        }
    }
    return array;
};

/**
 * @description подготавливаем даты для календаря
 *              для сравнения и получения допустимых дат
 * @param date
 * @param interval
 * @param periodData
 */
export const getTimeDisableValue = (
    date?: Dayjs,
    interval?: TaskProgressInterval,
    periodData?: IAssignmentPeriodsResponse,
): IGetTimeDisableValueReturn => {
    const { assignmentPeriod } = periodData ?? {};
    const periods = assignmentPeriod
        ? {
              datetimeStart: assignmentPeriod.datetimeStart,
              datetimeEnd: assignmentPeriod.datetimeEnd,
          }
        : undefined;

    const { datetimeStart, datetimeEnd } = interval || periods || {};
    dayjs.extend(timezone);
    const startDayjs = dayjs(datetimeStart).tz('Europe/Moscow');
    const end = dayjs(datetimeEnd).tz('Europe/Moscow');
    const currDate = dayjs().tz('Europe/Moscow');

    const start = startDayjs.isBefore(currDate, 'minutes') ? currDate : startDayjs;

    const isStartSame = date?.isSame(start.format('YYYY-MM-DD'), 'day');
    const isEndSame = date?.isSame(end.format('YYYY-MM-DD'), 'day');

    let startHour;
    let startMinute;
    let endHour;
    let endMinute;

    if (datetimeStart) {
        startHour = start.hour();
        startMinute = start.minute();
    }

    if (datetimeEnd) {
        endHour = end.hour();
        endMinute = end.minute();
    }

    return { startHour, startMinute, endMinute, endHour, isEndSame, isStartSame };
};
